import {
  useSelect,
  UseSelectState,
  UseSelectStateChange,
  UseSelectStateChangeOptions,
  GetPropsCommonOptions,
  UseComboboxGetToggleButtonPropsOptions,
  useMultipleSelection,
  UseMultipleSelectionGetDropdownProps,
  UseMultipleSelectionProps,
} from 'downshift';

import { useDropdown } from '.';
import { checkReferenceEquality, defaultLabelGetter } from '../helpers';
import { DefaultItem, GenericItem, UseMultiDropdownProps } from '../types';

/**
 * A hook used to manage all logic needed for a multiple select Dropdown.
 */
const useMultiDropdown = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLLabelElement
>({
  selectedItems: selectedItemsProp,
  itemToString = (item) => defaultLabelGetter(item as DefaultItem | null),
  ...props
}: UseMultiDropdownProps<TItem, TTarget>) => {
  checkReferenceEquality({ ...props, selectedItems: selectedItemsProp });

  const useMultipleSelectionArgs: UseMultipleSelectionProps<TItem> = {};
  if (selectedItemsProp) {
    // Setting this to undefined results in "cannot get length of undefined", so do this hack
    useMultipleSelectionArgs.selectedItems = selectedItemsProp;
  }

  const { getDropdownProps, ...useMultipleSelectionReturnValue } =
    useMultipleSelection<TItem>({
      ...useMultipleSelectionArgs,
      // Setting initial active index blocks downshift from focusing input on mount
      initialActiveIndex: 0,
      ...props,
    });

  const stateReducer = (
    state: UseSelectState<TItem>,
    { changes, type }: UseSelectStateChangeOptions<TItem>
  ): Partial<UseSelectState<TItem>> => {
    switch (type) {
      case useSelect.stateChangeTypes.MenuKeyDownEnter:
      case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
      case useSelect.stateChangeTypes.ItemClick:
        return {
          ...changes,
          isOpen: true, // keep menu open after selection
          highlightedIndex: state.highlightedIndex, // maintain highlighted item after selection
        };
      default:
        return changes;
    }
  };

  const onStateChange = ({
    type,
    selectedItem,
  }: UseSelectStateChange<TItem>) => {
    switch (type) {
      case useSelect.stateChangeTypes.MenuKeyDownEnter:
      case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
      case useSelect.stateChangeTypes.ItemClick:
        if (!selectedItem) {
          break;
        }

        if (
          !useMultipleSelectionReturnValue.selectedItems.includes(selectedItem)
        ) {
          useMultipleSelectionReturnValue.addSelectedItem(selectedItem);
        } else {
          useMultipleSelectionReturnValue.removeSelectedItem(selectedItem);
        }
        break;
      default:
        break;
    }
  };

  const {
    getToggleButtonProps: rawGetToggleButtonProps,
    isOpen,
    ...useDropdownReturnValue
  } = useDropdown({
    selectedItem: null,
    itemToString,
    stateReducer,
    onStateChange,
    ...props,
  });

  const getToggleButtonProps = (
    options?: UseComboboxGetToggleButtonPropsOptions &
      UseMultipleSelectionGetDropdownProps,
    extraOptions?: GetPropsCommonOptions
  ) =>
    rawGetToggleButtonProps({
      ...getDropdownProps(
        { preventKeyAction: isOpen, ...options },
        extraOptions
      ),
    });

  return {
    isOpen,
    getToggleButtonProps,
    ...useMultipleSelectionReturnValue,
    ...useDropdownReturnValue,
  };
};

export default Object.assign(useMultiDropdown, {
  stateChangeTypes: useDropdown.stateChangeTypes,
  multipleSelectionStateChangeTypes: useMultipleSelection.stateChangeTypes,
});
